import { trigger } from '@angular/animations';
import { AsyncPipe, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  inject,
} from '@angular/core';
import { AssetDto, GalleryDto, GalleryFeedbackOptions } from '@nusign/picflow-backend-types';
import { Observable, combineLatestWith, map, tap } from 'rxjs';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { billingScopes } from '../../../billing-consts';
import { AssetWatchersContextMenuComponent } from '../../../gallery/components/asset-watchers-context-menu/asset-watchers-context-menu.component';
import { GROWTHBOOK } from '../../../growthbook';
import { isMetaKey } from '../../../lib/is-meta-key';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { UserLike } from '../../../services/avatar.service';
import { OrganizationService } from '../../../services/organization.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { WatcherService, WatchingPerson } from '../../../services/watcher.service';
import { ClickOutsideDirective } from '../../../shared-extra/directives/click-outside.directive';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { EmojiSelectorComponent } from '../lazy/emoji-selector/emoji-selector.component';

@Component({
  selector: 'app-new-comment-thread',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './new-comment-thread.component.html',
  styleUrl: './new-comment-thread.component.scss',
  animations: [trigger('messageActions', fadeUpIf)],
  imports: [
    AsyncPipe,
    AvatarComponent,
    WysiwygDirective,
    TranslatePipe,
    ClickOutsideDirective,
    EmojiSelectorComponent,
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    SlicePipe,
    TooltipDirective,
    AvatarComponent,
    AssetWatchersContextMenuComponent,
  ],
})
export class NewCommentThreadComponent implements OnChanges {
  public growthBook = inject(GROWTHBOOK);
  public state = inject(StateService);
  private watcherService = inject(WatcherService);
  private permissionService = inject(PermissionService);
  private checkoutService = inject(PICFLOW_CHECKOUT);
  private organizationService = inject(OrganizationService);
  private check = inject(ChangeDetectorRef);

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    const metaKey = isMetaKey(event);
    if (event.target['getAttribute']?.('name') === 'new-thread') {
      if (event.key === 'Enter' && metaKey) this.postNewThread();
    }
  }
  @Input() me: UserLike;
  @Input() annotationMode = false;
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() enabledAndAllowedFeedbackOptions: GalleryFeedbackOptions[];
  @Input() commentPlacementMode: boolean;
  @Input() commentMessage: { text: string; addVideoFramePosition: boolean; private: boolean };
  @Input({ required: true }) galleryPermissions: GalleryDto['permissions'];
  @Input() isTeamMember: boolean;
  @Output() thread = new EventEmitter<{ text: string; addVideoFramePosition: boolean }>();
  @Output() toggleAnnotationMode = new EventEmitter<void>();
  @Output() placeComment = new EventEmitter<boolean>();

  watchers$: Observable<WatchingPerson[]>;
  teamWatchers$: Observable<WatchingPerson[]>;
  messageFocused = false;

  assetWatchers$: Observable<WatchingPerson[]>;
  galleryWatchers$: Observable<WatchingPerson[]>;

  ngOnChanges(): void {
    this.watchers$ = this.watcherService.getAssetWatchers(this.asset.id, this.gallery.id).pipe(
      map(watchers => (this.me ? watchers.filter(watcher => watcher.id !== this.me.id) : watchers)),
      tap(() => this.check.detectChanges())
    );
    if (this.isTeamMember) {
      this.teamWatchers$ = this.watcherService.getAssetWatchers(this.asset.id, this.gallery.id).pipe(
        map(watchers => (this.me ? watchers.filter(watcher => watcher.id !== this.me.id) : watchers)),
        combineLatestWith(this.organizationService.getMembers()),
        map(([watchers, members]) => {
          return watchers.filter(watcher => members.some(member => member.id === watcher.id));
        })
      );
    }
  }

  addEmoji(emoji: { emoji: { native: string } }) {
    this.commentMessage.text += emoji.emoji.native;
  }

  postNewThread() {
    if (!this.commentMessage.text) return;
    this.thread.emit(this.commentMessage);
    this.commentMessage.text = '';
    this.commentMessage.private = false;
  }

  togglePrivate() {
    if (this.permissionService.canDisplayElement(billingScopes.asset.privateComments)) {
      this.commentMessage.private = !this.commentMessage.private;
    } else {
      this.checkoutService.open(billingScopes.asset.privateComments);
    }
  }
}
